import { Box, Heading, HStack, Icon, Tooltip } from "@chakra-ui/react";
import { Info } from "lucide-react";
import { Helmet } from "react-helmet";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import _ from "lodash";

import DownloadReport from "../DownloadReport";
import FilterForm from "../FilterForm";
import Loader from "../loader";
import SelectBranchInfo from "../misc/SelectBranchInfo";
import DailyTableOnlyReport from "./DailyTableOnlyReport";
import ReportPresentationControls from "./ReportPresentationControls";
import ReportPresentationSummary from "./ReportPresentationSummary";
import { getReportDescription } from "../../store/helpers";
import { getDailyReportCsvAction } from "../../store/dailyReportExport";
import { useReportPresentation } from "../../hooks/useReportPresentation";
import { REPORT_TYPE_IDS_WITH_TABLE_GROUPING } from "../../store/constants";

const defaultMapReports = ({ reports, branchScoped, branchId, organizationId }) => {
  if (branchScoped) {
    let scopedReports = reports;
    if (branchId?.length > 0) {
      scopedReports = scopedReports.filter((report) => report.branchId === branchId);
    }
    return scopedReports.map((report) => report.data) || [];
  }

  return reports.find((report) => report.organizationId === organizationId)?.data || [];
};

const DailyGeneratedReportPage = ({
  branchScoped = false,
  children,
  columnDefinitions,
  defaultSortColumn,
  descriptionKey,
  helmetTitle,
  mapReports = defaultMapReports,
  reportTypeId,
  showFilterForm = true,
  showTitle = 1,
  title,
}) => {
  const { branchId, loading, organizationId, reportRange, reportsData } = useSelector(
    (state) => state.defaultReducer
  );
  const reports = useMemo(
    () => reportsData.filter((report) => report.reportTypeId === reportTypeId),
    [reportTypeId, reportsData]
  );
  const data = useMemo(
    () => mapReports({ branchId, branchScoped, organizationId, reports }),
    [branchId, branchScoped, mapReports, organizationId, reports]
  );
  const {
    effectiveRenderConfig,
    isLoadingRenderSetting,
    isSaving,
    presentationSummaryItems,
    saveRenderConfig,
    setRenderConfig,
  } = useReportPresentation({
    reportTypeId,
    columnDefinitions,
  });
  const hasData = !_.isEmpty(data);
  const supportsTableGrouping = REPORT_TYPE_IDS_WITH_TABLE_GROUPING.includes(reportTypeId);
  const downloadCsv = useMemo(
    () =>
      getDailyReportCsvAction({
        reportTypeId,
        data,
        renderConfig: effectiveRenderConfig,
      }),
    [data, effectiveRenderConfig, reportTypeId]
  );

  return (
    <Box>
      {showTitle === 1 ? (
        <Helmet>
          <title>{helmetTitle}</title>
        </Helmet>
      ) : null}
      {showFilterForm ? <FilterForm /> : null}
      {loading || isLoadingRenderSetting ? (
        <Loader />
      ) : hasData ? (
        <>
          <Box mb="20px">
            <HStack flexDirection="row" justifyContent="space-between" alignItems="start">
              <Heading fontSize="34px" color="#222" fontWeight="700">
                {title}
                <Tooltip.Root>
                  <Tooltip.Trigger asChild>
                    <Icon as={Info} w={5} h={5} ml={2} cursor="pointer" />
                  </Tooltip.Trigger>
                  <Tooltip.Positioner>
                    <Tooltip.Content>
                      {getReportDescription(reportRange)[descriptionKey]}
                    </Tooltip.Content>
                  </Tooltip.Positioner>
                </Tooltip.Root>
              </Heading>
              <DownloadReport
                reportRange={reportRange}
                reportTypeId={reportTypeId}
                renderConfig={effectiveRenderConfig}
                onSave={saveRenderConfig}
                isSaving={isSaving}
                onDownloadCsv={downloadCsv}
              />
            </HStack>
          </Box>
          <ReportPresentationControls
            renderConfig={effectiveRenderConfig}
            onChange={setRenderConfig}
            columnDefinitions={columnDefinitions}
            defaultSortColumn={defaultSortColumn}
            supportsTableGrouping={supportsTableGrouping}
            tableControlsRequireChartsOff={true}
          />
          <ReportPresentationSummary items={presentationSummaryItems} />
          {!effectiveRenderConfig.charts.enabled ? (
            <DailyTableOnlyReport
              columnDefinitions={columnDefinitions}
              data={data}
              renderConfig={effectiveRenderConfig}
              reportTypeId={reportTypeId}
              onRenderConfigChange={setRenderConfig}
            />
          ) : (
            children({
              data,
              renderConfig: effectiveRenderConfig,
              onRenderConfigChange: setRenderConfig,
            })
          )}
        </>
      ) : (
        <SelectBranchInfo message="Update Filters and Hit Search to retrieve data" />
      )}
    </Box>
  );
};

export default DailyGeneratedReportPage;
